/**
 * MapLibre style generation.
 *
 * The style is a pure function of (chart manifest, boat profile, display
 * options). That matters for two reasons:
 *
 *  1. Changing the boat's draft re-shades the chart and re-promotes hazards
 *     immediately, because the safety depth is compiled into the expressions.
 *  2. The safety-critical rendering rules are unit-testable without a map,
 *     a GPU, or a network. See style.test.ts.
 *
 * Band ordering: sources are emitted in ascending band order and every layer of
 * band N is drawn beneath every layer of band N+1, so the largest-scale chart
 * available at any point wins. Hazards are drawn above ALL bands, last.
 */

import type {
  CircleLayerSpecification,
  ExpressionSpecification,
  FillLayerSpecification,
  FilterSpecification,
  LayerSpecification,
  LineLayerSpecification,
  StyleSpecification,
  SymbolLayerSpecification,
} from 'maplibre-gl';

import { type BoatProfile, depthBands, safetyDepth } from '../core/boat';
import { CHART_ATTRIBUTION } from '../core/legal';
import {
  METRES_PER_FATHOM,
  METRES_PER_FOOT,
  type DepthUnit,
} from '../core/units';
import { PALETTES, type ChartPalette, type PaletteName } from './palette';

/**
 * Metres per display unit, for converting depths inside style expressions.
 * Tiles stay metric; a change of unit rebuilds the style, never the charts.
 */
const METRES_PER_DEPTH_UNIT: Record<DepthUnit, number> = {
  m: 1,
  ft: METRES_PER_FOOT,
  fathom: METRES_PER_FATHOM,
};

export interface ChartSource {
  band: number;
  file: string;
  minzoom: number;
  maxzoom: number;
  cells: number;
  bytes: number;
  /**
   * Which collection this source came from. Set by `mergeCollections` when more
   * than one region is loaded; absent for a single-collection manifest.
   */
  collectionId?: string;
}

export interface ChartManifest {
  id: string;
  name: string;
  schemaVersion: number;
  generatedAt?: string;
  layers: string[];
  sources: ChartSource[];
}

export interface StyleOptions {
  manifest: ChartManifest;
  /** Base URL the .pmtiles files are served from, with trailing slash. */
  chartBaseUrl: string;
  boat: BoatProfile;
  palette?: PaletteName;
  /** Spot soundings can be turned off; hazards can never be. */
  showSoundings?: boolean;
  showSeabed?: boolean;
  /** Place names and feature labels. */
  showLabels?: boolean;
  /** Unit that depth labels are drawn in. Tiles remain metric regardless. */
  depthUnit?: DepthUnit;
  /**
   * Glyph URL template for text rendering. All text layers are omitted when
   * absent, so the chart still draws if fonts are missing rather than failing.
   */
  glyphsUrl?: string;
}

/**
 * S-57 WATLEV values that mean the feature is at or above the waterline at some
 * state of tide: 1 partly submerged, 2 always dry, 4 covers and uncovers,
 * 5 awash. Any of these is a danger regardless of what VALSOU says.
 */
export const DRYING_WATER_LEVELS = [1, 2, 4, 5];

/**
 * Smallest radius, in pixels, a charted danger is ever drawn at.
 *
 * This used to be 5 px around a magenta ring with a white halo behind it, drawn
 * on every hazard dangerous to this boat that sat in otherwise-safe water. On a
 * rocky coast that is a great many hazards, and the result was a field of
 * identical magenta circles with no way to tell which one mattered — the symbol
 * was on screen so constantly that it stopped carrying information.
 *
 * Every charted danger still leaves a mark at every zoom, never filtered on
 * SCAMIN (rule 4). The mark is now small and quiet. The loud magenta symbol has
 * moved to the dangers that lie in the boat's own path — see
 * `src/chart/forwardDangers.ts` — where it answers a question the skipper is
 * actually asking.
 */
export const MIN_DANGER_RADIUS_PX = 2.5;


/**
 * MapLibre source id for a chart source.
 *
 * Exported because the route checker builds layer ids from the same convention;
 * two places inventing the same string independently is how a layer silently
 * stops being queried.
 *
 * The collection id is included only when present, which keeps ids short and
 * readable for the common single-region case while guaranteeing uniqueness once
 * regions are merged — two collections both publishing a band 5 would otherwise
 * collide and one would silently replace the other.
 */
export function chartSourceId(source: Pick<ChartSource, 'band' | 'collectionId'>): string {
  return source.collectionId
    ? `chart-${source.collectionId}-band${source.band}`
    : `chart-band${source.band}`;
}

/**
 * Fill colour for a depth area, as a function of the boat's safety depth.
 *
 * A depth area with no stated shallow limit is shaded as unsafe. That is
 * deliberate: an unknown depth is a reason for caution, and shading it as deep
 * water would be the one failure mode this whole app exists to prevent.
 */
export function depthFillExpression(boat: BoatProfile, palette: ChartPalette): ExpressionSpecification {
  const [, safety, deep] = depthBands(boat);
  return [
    'case',
    ['!', ['has', 'drval1']], palette.depthUnsafe,
    ['<', ['get', 'drval1'], 0], palette.depthDrying,
    ['<', ['get', 'drval1'], safety], palette.depthUnsafe,
    ['<', ['get', 'drval1'], deep], palette.depthCaution,
    palette.depthDeep,
  ];
}

/**
 * Which charted hazards are dangerous to THIS boat.
 *
 * Three ways a hazard qualifies, and the first is the one that matters most:
 *   - no surveyed least depth at all,
 *   - a least depth at or above the safety depth,
 *   - a water level meaning it dries, is awash, or covers and uncovers.
 */
export function dangerousHazardFilter(boat: BoatProfile): FilterSpecification {
  return [
    'any',
    ['!', ['has', 'valsou']],
    ['<=', ['get', 'valsou'], safetyDepth(boat)],
    ['in', ['get', 'watlev'], ['literal', DRYING_WATER_LEVELS]],
  ] as FilterSpecification;
}

/** Hazards deep enough to pass over safely — drawn small and quietly. */
export function clearHazardFilter(boat: BoatProfile): FilterSpecification {
  return ['!', dangerousHazardFilter(boat)] as FilterSpecification;
}

/**
 * True where the water AROUND the hazard is safe for this boat.
 *
 * `area_drval1` is the shallowest charted depth of the depth area containing the
 * hazard, recorded at build time. Absent means no depth area covers it, which is
 * treated as safe water so the hazard is still promoted — the conservative
 * reading of "unknown".
 */
function inOtherwiseSafeWater(boat: BoatProfile): ExpressionSpecification {
  return [
    'any',
    ['!', ['has', 'area_drval1']],
    ['>', ['get', 'area_drval1'], safetyDepth(boat)],
  ];
}

/**
 * ISOLATED dangers: dangerous to this boat AND sitting in water that otherwise
 * looks passable. These get the full magenta symbol at every zoom.
 *
 * The qualifier matters enormously. Promoting every shallow hazard turns a rocky
 * coastline into a wall of magenta that hides the chart underneath — measured on
 * CHS V-ATL-B, where most hazards are drying rocks inside water already shaded
 * unsafe. The IHO rule marks dangers lying *within the safe water defined by the
 * safety contour*, because those are the ones that surprise you. A rock inside
 * an area already coloured "do not go here" needs no extra symbol.
 */
export function isolatedDangerFilter(boat: BoatProfile): FilterSpecification {
  return ['all', dangerousHazardFilter(boat), inOtherwiseSafeWater(boat)] as FilterSpecification;
}

/**
 * Dangerous hazards that lie inside already-unsafe water. Still drawn — never
 * hidden — but small and unobtrusive, because the depth shading is already
 * carrying the warning.
 */
export function dangerInShallowWaterFilter(boat: BoatProfile): FilterSpecification {
  return [
    'all',
    dangerousHazardFilter(boat),
    ['!', inOtherwiseSafeWater(boat)],
  ] as FilterSpecification;
}

export function unsafeClearanceFilter(boat: BoatProfile): FilterSpecification {
  if (boat.airDraftM === undefined) return ['==', ['literal', 1], ['literal', 0]] as FilterSpecification;
  return [
    'any',
    ['!', ['has', 'verclr']],
    ['<=', ['get', 'verclr'], boat.airDraftM],
  ] as FilterSpecification;
}

// ---------------------------------------------------------------------------

function baseChartLayers(
  chartSource: ChartSource,
  palette: ChartPalette,
  boat: BoatProfile,
  options: StyleOptions,
): LayerSpecification[] {
  const source = chartSourceId(chartSource);
  const p = (name: string): string => `${source}-${name}`;
  const layers: LayerSpecification[] = [];

  // --- Water ---------------------------------------------------------------
  layers.push({
    id: p('depth-area'),
    type: 'fill',
    source,
    'source-layer': 'depth_area',
    paint: { 'fill-color': depthFillExpression(boat, palette), 'fill-antialias': false },
  } satisfies FillLayerSpecification);

  layers.push({
    id: p('inland-water'),
    type: 'fill',
    source,
    'source-layer': 'inland_water',
    paint: { 'fill-color': palette.depthCaution },
  } satisfies FillLayerSpecification);

  if (options.showSeabed) {
    layers.push({
      id: p('seabed'),
      type: 'fill',
      source,
      'source-layer': 'seabed_area',
      paint: { 'fill-color': palette.landEdge, 'fill-opacity': 0.08 },
    } satisfies FillLayerSpecification);
  }

  // --- Contours ------------------------------------------------------------
  layers.push({
    id: p('depth-contour'),
    type: 'line',
    source,
    'source-layer': 'depth_contour',
    paint: { 'line-color': palette.depthContour, 'line-width': 0.7 },
  } satisfies LineLayerSpecification);

  // The safety contour is the single most important line on the chart, so it is
  // drawn heavier than every other contour and in its own colour.
  layers.push({
    id: p('safety-contour'),
    type: 'line',
    source,
    'source-layer': 'depth_contour',
    filter: ['<=', ['get', 'valdco'], safetyDepth(boat)] as FilterSpecification,
    paint: {
      'line-color': palette.safetyContourLine,
      'line-width': ['interpolate', ['linear'], ['zoom'], 8, 1.2, 14, 2.4],
    },
  } satisfies LineLayerSpecification);

  // --- Land ----------------------------------------------------------------
  layers.push({
    id: p('land'),
    type: 'fill',
    source,
    'source-layer': 'land_area',
    paint: { 'fill-color': palette.land },
  } satisfies FillLayerSpecification);

  layers.push({
    id: p('built-up'),
    type: 'fill',
    source,
    'source-layer': 'built_up_area',
    paint: { 'fill-color': palette.builtUp },
  } satisfies FillLayerSpecification);

  layers.push({
    id: p('coastline'),
    type: 'line',
    source,
    'source-layer': 'coastline',
    paint: { 'line-color': palette.landEdge, 'line-width': 1 },
  } satisfies LineLayerSpecification);

  layers.push({
    id: p('shoreline-construction'),
    type: 'line',
    source,
    'source-layer': 'shoreline_construction',
    paint: { 'line-color': palette.landEdge, 'line-width': 1.4 },
  } satisfies LineLayerSpecification);

  // --- Regulated areas -----------------------------------------------------
  // Regulated areas are drawn as a boundary only, with NO fill.
  //
  // This follows chart convention — paper charts and ECDIS both mark these with
  // a dashed magenta limit line — and it is also the only thing that works.
  // Filling them was tried twice: first across all bands, then from a single
  // band. Both turned the water pink, because regulated areas *overlap*. A
  // harbour carries harbour limits, anchorages and caution areas stacked on top
  // of one another, so any translucent fill compounds with itself no matter how
  // few layers draw it. Measured at Halifax: two overlapping polygons at a
  // single point inside one band.
  //
  // The depth shading underneath is the safety-critical information. Nothing
  // decorative gets to sit on top of it.
  layers.push({
    id: p('restricted-line'),
    type: 'line',
    source,
    'source-layer': 'restricted_area',
    minzoom: 8,
    paint: {
      'line-color': palette.restrictedLine,
      'line-width': ['interpolate', ['linear'], ['zoom'], 8, 0.8, 14, 1.4],
      'line-dasharray': [4, 3],
    },
  } satisfies LineLayerSpecification);

  layers.push({
    id: p('traffic-scheme'),
    type: 'line',
    source,
    'source-layer': 'traffic_scheme',
    paint: { 'line-color': palette.trafficLine, 'line-width': 1.2, 'line-dasharray': [6, 3] },
  } satisfies LineLayerSpecification);

  layers.push({
    id: p('cable-pipeline'),
    type: 'line',
    source,
    'source-layer': 'cable_pipeline',
    paint: { 'line-color': palette.cableLine, 'line-width': 1, 'line-dasharray': [1, 2] },
  } satisfies LineLayerSpecification);

  // --- Aids to navigation --------------------------------------------------
  layers.push({
    id: p('buoy'),
    type: 'circle',
    source,
    'source-layer': 'buoy',
    minzoom: 10,
    paint: {
      'circle-radius': ['interpolate', ['linear'], ['zoom'], 10, 3, 15, 6],
      'circle-color': buoyColourExpression(palette),
      'circle-stroke-color': palette.textHalo,
      'circle-stroke-width': 1,
    },
  } satisfies CircleLayerSpecification);

  layers.push({
    id: p('beacon'),
    type: 'circle',
    source,
    'source-layer': 'beacon',
    minzoom: 11,
    paint: {
      'circle-radius': ['interpolate', ['linear'], ['zoom'], 11, 2.5, 15, 5],
      'circle-color': palette.buoyDefault,
      'circle-stroke-color': palette.textHalo,
      'circle-stroke-width': 1,
    },
  } satisfies CircleLayerSpecification);

  layers.push({
    id: p('light'),
    type: 'circle',
    source,
    'source-layer': 'light',
    minzoom: 9,
    paint: {
      'circle-radius': ['interpolate', ['linear'], ['zoom'], 9, 3, 15, 7],
      'circle-color': palette.lightFlare,
      'circle-opacity': 0.85,
      'circle-stroke-color': palette.textHalo,
      'circle-stroke-width': 1,
    },
  } satisfies CircleLayerSpecification);

  return layers;
}

function buoyColourExpression(palette: ChartPalette): ExpressionSpecification {
  // S-57 COLOUR: 3 = red, 4 = green, 6 = yellow. Multi-coloured buoys arrive as
  // "3,4"; the leading colour is the one a skipper reads first.
  return [
    'case',
    ['==', ['slice', ['concat', ['coalesce', ['get', 'colour'], ''], ','], 0, 1], '3'], palette.buoyRed,
    ['==', ['slice', ['concat', ['coalesce', ['get', 'colour'], ''], ','], 0, 1], '4'], palette.buoyGreen,
    ['==', ['slice', ['concat', ['coalesce', ['get', 'colour'], ''], ','], 0, 1], '6'], palette.buoyYellow,
    palette.buoyDefault,
  ];
}

/**
 * Hazard layers. Emitted once, above every band.
 *
 * NOTE what is absent: there is no `minzoom`, and no filter on `scamin`. A rock
 * that is dangerous to this boat is drawn at every zoom the map can reach, at a
 * radius that never falls below MIN_DANGER_RADIUS_PX. This is the mitigation for
 * the documented over-scaling failure mode — see docs/ARCHITECTURE.md §2.1.
 */
function hazardLayers(
  sources: readonly ChartSource[],
  palette: ChartPalette,
  boat: BoatProfile,
): LayerSpecification[] {
  const layers: LayerSpecification[] = [];

  for (const chartSource of sources) {
    const source = chartSourceId(chartSource);

    // Hazards that are safely deep for this boat: small, muted, informational.
    layers.push({
      id: `${source}-hazard-clear`,
      type: 'circle',
      source,
      'source-layer': 'hazard',
      minzoom: 12,
      filter: clearHazardFilter(boat),
      paint: {
        'circle-radius': 3,
        'circle-color': 'transparent',
        'circle-stroke-color': palette.soundingSafe,
        'circle-stroke-width': 1,
      },
    } satisfies CircleLayerSpecification);

    // Dangerous, but inside water already shaded unsafe. Drawn small: the
    // shading carries the warning, and a full symbol here would bury the chart.
    // Never hidden entirely — a charted danger always leaves a mark.
    layers.push({
      id: `${source}-hazard-shallow`,
      type: 'circle',
      source,
      'source-layer': 'hazard',
      filter: dangerInShallowWaterFilter(boat),
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 8, 1.6, 16, 3.5],
        'circle-color': palette.danger,
        'circle-opacity': 0.9,
      },
    } satisfies CircleLayerSpecification);

    // Isolated dangers: dangerous to this boat and sitting in water that looks
    // passable. Drawn a little larger than the ones inside already-shaded water
    // and outlined so they read against any depth shade — but a mark, not a
    // symbol. The ring-and-halo treatment they used to get is now reserved for
    // the ones lying in the boat's path.
    layers.push({
      id: `${source}-hazard-danger`,
      type: 'circle',
      source,
      'source-layer': 'hazard',
      filter: isolatedDangerFilter(boat),
      paint: {
        'circle-radius': [
          'interpolate', ['linear'], ['zoom'],
          0, MIN_DANGER_RADIUS_PX,
          10, MIN_DANGER_RADIUS_PX + 0.5,
          16, MIN_DANGER_RADIUS_PX + 2,
        ],
        'circle-color': palette.danger,
        'circle-stroke-color': palette.dangerHalo,
        'circle-stroke-width': 1,
      },
    } satisfies CircleLayerSpecification);

    // Overhead obstructions too low for this boat's air draft.
    layers.push({
      id: `${source}-clearance-danger`,
      type: 'line',
      source,
      'source-layer': 'overhead_obstruction',
      filter: unsafeClearanceFilter(boat),
      paint: { 'line-color': palette.danger, 'line-width': 3 },
    } satisfies LineLayerSpecification);
  }

  return layers;
}

const FONT_REGULAR = ['Noto Sans Regular'];
const FONT_ITALIC = ['Noto Sans Italic'];

/**
 * Sounding labels.
 *
 * Depths are shown in the user's chosen unit, converted here in the expression
 * rather than in the tiles — tiles stay metric so a change of unit never means a
 * chart rebuild.
 */
function soundingLayers(
  sources: readonly ChartSource[],
  palette: ChartPalette,
  boat: BoatProfile,
  depthUnit: DepthUnit,
): LayerSpecification[] {
  const safety = safetyDepth(boat);
  const perUnit = METRES_PER_DEPTH_UNIT[depthUnit];
  const asDisplay: ExpressionSpecification = ['/', ['get', 'depth'], perUnit];
  // S-52 shows soundings shallower than 31 m to a decimetre and deeper ones as
  // whole units — precision where it changes decisions, and none where it does
  // not. The threshold is a depth in METRES, so it is compared against the
  // stored value, not the converted one. Feet are always whole: a tenth of a
  // foot is far below the accuracy of any survey.
  const SHALLOW_DETAIL_LIMIT_M = 31;
  const label: ExpressionSpecification = depthUnit === 'ft'
    ? ['to-string', ['round', asDisplay]]
    : [
      'case',
      ['<', ['abs', ['get', 'depth']], SHALLOW_DETAIL_LIMIT_M],
      ['to-string', ['/', ['round', ['*', asDisplay, 10]], 10]],
      ['to-string', ['round', asDisplay]],
    ];

  return sources.map((chartSource) => ({
    id: `${chartSourceId(chartSource)}-sounding`,
    type: 'symbol',
    source: chartSourceId(chartSource),
    'source-layer': 'sounding',
    minzoom: 12,
    layout: {
      'text-field': label,
      'text-font': FONT_REGULAR,
      'text-size': ['interpolate', ['linear'], ['zoom'], 12, 9, 16, 12],
      'text-allow-overlap': false,
      'text-padding': 4,
      // Shallow soundings win label collisions. When the map cannot fit every
      // sounding, the ones that matter are the shallow ones.
      'symbol-sort-key': ['get', 'depth'],
    },
    paint: {
      'text-color': [
        'case',
        ['<=', ['get', 'depth'], safety], palette.soundingUnsafe,
        palette.soundingSafe,
      ],
      'text-halo-color': palette.textHalo,
      'text-halo-width': 1.2,
    },
  } satisfies SymbolLayerSpecification));
}

/**
 * Place names and feature labels.
 *
 * Chart convention drives the typography: water features are italic, land
 * features upright. Minimum zooms are set so each kind of name appears at the
 * scale where it is useful — sea areas when you are looking at a region, buoy
 * names only when you are close enough to be looking for the buoy itself.
 *
 * Labels are emitted per band, so the same place can be named by more than one
 * chart. MapLibre's collision detection suppresses the duplicate, and the
 * larger-scale band wins because it is drawn later.
 */
function placeNameLayers(
  sources: readonly ChartSource[],
  palette: ChartPalette,
): LayerSpecification[] {
  const layers: LayerSpecification[] = [];

  const named = ['has', 'objnam'] as FilterSpecification;
  const textPaint = {
    'text-color': palette.text,
    'text-halo-color': palette.textHalo,
    'text-halo-width': 1.4,
  };

  for (const chartSource of sources) {
    const source = chartSourceId(chartSource);
    const p = (name: string): string => `${source}-${name}`;

    // Water bodies: italic, widely spaced, visible early — these orient you.
    layers.push({
      id: p('label-sea-area'),
      type: 'symbol',
      source,
      'source-layer': 'sea_area',
      minzoom: 7,
      filter: named,
      layout: {
        'text-field': ['get', 'objnam'],
        'text-font': FONT_ITALIC,
        'text-size': ['interpolate', ['linear'], ['zoom'], 7, 11, 14, 15],
        'text-letter-spacing': 0.12,
        'text-max-width': 8,
        'text-padding': 8,
      },
      paint: { ...textPaint, 'text-color': palette.safetyContourLine },
    } satisfies SymbolLayerSpecification);

    layers.push({
      id: p('label-built-up'),
      type: 'symbol',
      source,
      'source-layer': 'built_up_area',
      minzoom: 9,
      filter: named,
      layout: {
        'text-field': ['get', 'objnam'],
        'text-font': FONT_REGULAR,
        'text-size': ['interpolate', ['linear'], ['zoom'], 9, 11, 15, 14],
        'text-max-width': 8,
      },
      paint: textPaint,
    } satisfies SymbolLayerSpecification);

    layers.push({
      id: p('label-land'),
      type: 'symbol',
      source,
      'source-layer': 'land_area',
      minzoom: 11,
      filter: named,
      layout: {
        'text-field': ['get', 'objnam'],
        'text-font': FONT_REGULAR,
        'text-size': ['interpolate', ['linear'], ['zoom'], 11, 10, 15, 13],
        'text-max-width': 8,
      },
      paint: textPaint,
    } satisfies SymbolLayerSpecification);

    layers.push({
      id: p('label-anchorage'),
      type: 'symbol',
      source,
      'source-layer': 'anchorage',
      minzoom: 12,
      filter: named,
      layout: {
        'text-field': ['get', 'objnam'],
        'text-font': FONT_ITALIC,
        'text-size': 11,
        'text-max-width': 8,
      },
      paint: textPaint,
    } satisfies SymbolLayerSpecification);

    layers.push({
      id: p('label-landmark'),
      type: 'symbol',
      source,
      'source-layer': 'landmark',
      minzoom: 13,
      filter: named,
      layout: {
        'text-field': ['get', 'objnam'],
        'text-font': FONT_REGULAR,
        'text-size': 10,
        'text-offset': [0, 0.9],
        'text-anchor': 'top',
        'text-max-width': 8,
      },
      paint: textPaint,
    } satisfies SymbolLayerSpecification);

    // Aid names last and latest: useful only when you are hunting for the mark.
    layers.push({
      id: p('label-aid'),
      type: 'symbol',
      source,
      'source-layer': 'buoy',
      minzoom: 14,
      filter: named,
      layout: {
        'text-field': ['get', 'objnam'],
        'text-font': FONT_REGULAR,
        'text-size': 10,
        'text-offset': [0.8, 0],
        'text-anchor': 'left',
      },
      paint: textPaint,
    } satisfies SymbolLayerSpecification);
  }

  return layers;
}

/**
 * Depth contour labels, placed along the line as on a paper chart.
 * Only the safety contour and rounder values are labelled; labelling every
 * contour buries the chart.
 */
function contourLabelLayers(
  sources: readonly ChartSource[],
  palette: ChartPalette,
  depthUnit: DepthUnit,
): LayerSpecification[] {
  const perUnit = METRES_PER_DEPTH_UNIT[depthUnit];
  return sources.map((chartSource) => ({
    id: `${chartSourceId(chartSource)}-label-contour`,
    type: 'symbol',
    source: chartSourceId(chartSource),
    'source-layer': 'depth_contour',
    minzoom: 12,
    filter: ['has', 'valdco'] as FilterSpecification,
    layout: {
      'symbol-placement': 'line',
      'text-field': ['to-string', ['round', ['/', ['get', 'valdco'], perUnit]]],
      'text-font': FONT_REGULAR,
      'text-size': 9,
      'symbol-spacing': 320,
      'text-padding': 6,
    },
    paint: {
      'text-color': palette.depthContour,
      'text-halo-color': palette.textHalo,
      'text-halo-width': 1.4,
    },
  } satisfies SymbolLayerSpecification));
}

/**
 * Build the complete MapLibre style.
 *
 * Chart sources are ordered by band ascending; layers follow the same order, so
 * a harbour chart draws over an approach chart draws over a general chart.
 * Wherever a larger-scale chart has no coverage, the smaller-scale one below
 * shows through — which is what keeps the display continuous across the gaps in
 * a published chart collection.
 */
export function buildChartStyle(options: StyleOptions): StyleSpecification {
  const {
    manifest, chartBaseUrl, boat,
    palette: paletteName = 'day',
    showSoundings = true,
    showLabels = true,
    depthUnit = 'm',
    glyphsUrl,
  } = options;

  const palette = PALETTES[paletteName];
  const bands = [...manifest.sources].sort((a, b) => a.band - b.band);

  const sources: StyleSpecification['sources'] = {};
  for (const source of bands) {
    sources[chartSourceId(source)] = {
      type: 'vector',
      url: `pmtiles://${chartBaseUrl}${source.file}`,
      minzoom: source.minzoom,
      maxzoom: source.maxzoom,
      // CHS requires a derived product to say it is not an official chart and
      // to avoid anything that reads as a CHS product. See src/core/legal.ts.
      attribution: CHART_ATTRIBUTION,
    };
  }

  const layers: LayerSpecification[] = [
    // Everything not covered by a depth area is unsurveyed water, not deep
    // water. It is painted as the caution shade so blank areas never read as
    // safe.
    {
      id: 'background',
      type: 'background',
      paint: { 'background-color': palette.depthCaution },
    },
  ];

  for (const source of bands) {
    layers.push(...baseChartLayers(source, palette, boat, options));
  }


  // Text needs glyphs. Without them MapLibre errors on every symbol layer, so
  // the chart is drawn without labels rather than half-broken.
  if (glyphsUrl) {
    if (showLabels) {
      layers.push(...contourLabelLayers(bands, palette, depthUnit));
      layers.push(...placeNameLayers(bands, palette));
    }
    if (showSoundings) {
      layers.push(...soundingLayers(bands, palette, boat, depthUnit));
    }
  }

  layers.push(...hazardLayers(bands, palette, boat));

  const style: StyleSpecification = {
    version: 8,
    name: `WebNav — ${manifest.name}`,
    sources,
    layers,
  };
  if (glyphsUrl) style.glyphs = glyphsUrl;
  return style;
}

/** Layer ids that respond to a feature tap, most specific first. */
export function queryableLayerIds(manifest: ChartManifest): string[] {
  const bands = [...manifest.sources].sort((a, b) => b.band - a.band);
  const suffixes = [
    // Both dangerous-hazard layers are tappable. They are small marks now, and a
    // small mark you cannot interrogate is worse than no mark at all.
    'hazard-danger', 'hazard-shallow', 'hazard-clear', 'clearance-danger',
    'buoy', 'beacon', 'light', 'sounding',
    'restricted-line', 'traffic-scheme', 'cable-pipeline',
    'depth-area',
  ];
  return bands.flatMap((source) => suffixes.map((suffix) => `${chartSourceId(source)}-${suffix}`));
}
